import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import toastr from 'toastr';

import { ORG_TYPES, USER_TYPES } from '../../common/constants';

import Requester from '../../helpers/requester';
import APIConstants from '../../helpers/api_constants';

import SignupForm from './SignupForm';
import PaymentSourceFormWrapper from './PaymentSourceFormWrapper';

/**
 * Component for User Signup and completing signup process.
 * Handles save/update callbacks for the actual form depending on
 * if we're creating or editing a user
 * @prop orgType - either "business" or "recipient"
 * @prop user - the user we're editing. Will be null if this is a new registration
 * @prop userType - the type of user we're editing
 */
const SignupFormWrapper = ({ orgType, user, userType }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [currStep, setCurrStep] = useState(0);
  const [tempUserInfo, setTempUserInfo] = useState(null);

  // Call updateUser or createUser when userInfo is updated and the step remains at 0
  useEffect(() => {
    if (currStep === 0 && userInfo) {
      if (user) {
        updateUser();
      } else {
        createUser();
      }
    }
  }, [userInfo, currStep]);

  /*
   * Called on click when completing the signup form. It will always store
   * the user information in the state and will only update step for scenarios where
   * users are required to enter payment information
   */
  const savesUserData = (userData) => {
    if (orgType === ORG_TYPES.BUSINESS) {
      if (!user || userType === USER_TYPES.REGIONAL_MANAGER) {
        if(!user) {
          // this is the first organization user. Send information to backend
          // for sales to follow-up if they don't complete signup
          Requester.post(
            APIConstants.users.send_notification_to_sales,
            userData,
            () => {},
            (response,_) => { console.log(response.errors) }
          );
        }

        // The first organization user and all regional managers must fill
        // out the payment source information
        setCurrStep(currStep + 1);
        setUserInfo(userData);
        setTempUserInfo(userData); // Store in temporary state
      } else {
        // Any other user types should not input payment source information
        setUserInfo(userData);
        setTempUserInfo(userData); // Store in temporary state
      }
    } else {
      setUserInfo(userData);
      setTempUserInfo(userData); // Store in temporary state
    }
  };

  /**
   * Callback for form submit when we are editing a user
   */
  const updateUser = (paymentMethod, token) => {
    setIsLoading(true);
    const {
      organization_id: organizationId,
      id: userId,
    } = user;

    const failure = (response, responseCode) => {
      let errorMessage = 'Please try again or reach out to our support team for more help!';

      if (responseCode === 400) {
        [errorMessage] = response.errors;
      }

      toastr.error(
        errorMessage,
        'There was an error completing your signup process.',
        { closeButton: true, timeOut: 0 },
      );
      setIsLoading(false);
    };

    const success = () => {
      setIsLoading(false);
      advanceToDashboard(organizationId);
      window.scrollTo(0, 0);
    };

    const params = { user: userInfo };

    // The payment_method and token will only be included for specific user types in
    // specific scenarios. If it is null, we do not want it included in the params.
    if (paymentMethod && token) {
      params.payment_method = paymentMethod;
      params.token = token;
    }

    Requester.update(APIConstants.users.finish_signup(userId), params, success, failure);
  };

  /**
   * Callback for form submit for creating a user
   */
  const createUser = (paymentMethod, token) => {
    setIsLoading(true);
    const failure = (response, statusCode) => {
      let errorMessage = 'Please try again or reach out to our support team for more help!';

      if (statusCode === 400) {
        [errorMessage] = response.errors;
      }

      toastr.error(
        errorMessage,
        'There was an error completing your signup process.',
        { closeButton: true, timeOut: 0 },
      );
      setIsLoading(false);
    };

    const success = (res) => {
      setIsLoading(false);
      advanceToDashboard(res.data.organization_id);
      window.scrollTo(0, 0);

      // Google Analytics Event Log
      const eventType = `${orgType}_sign_up_complete`;
    };

    const params = { user: userInfo };

    // The payment_method and token will only be included for specific user types in
    // specific scenarios. If it is null, we do not want it included in the params.
    if (paymentMethod && token) {
      params.payment_method = paymentMethod;
      params.token = token;
    }

    // registrations_controller#create
    Requester.post(APIConstants.sessions.signup, params, success, failure);
  };

  /**
   * Callback for going back to the signup form. We have to set the user info to null
   * or else the useEffect will complete the registration
   */
  const goBack = () => {
    setCurrStep(currStep - 1);
    setUserInfo(null);
  };

  /**
   * After successfully creating or editing a user, go to org dashboard page
   */
  const advanceToDashboard = (orgId) => {
    window.location = `/organizations/${orgId}`;
  };

  const inputStepToRender = () => {
    if (currStep === 0) {
      return (
        <SignupForm
          save={savesUserData}
          orgType={orgType}
          user={user}
          initialData={tempUserInfo}
        />
      );
    }
    return (
      <PaymentSourceFormWrapper
        onSubmit={user ? updateUser : createUser}
        goBack={goBack}
      />
    );
  };

  return (
    <div>
      { isLoading && (
        <div className="loading-container fullscreen">
          <span className="visually-hidden" aria-live="polite">Loading...</span>
          <div className="loading" />
        </div>
      )}
      { inputStepToRender() }
    </div>
  );
};

SignupFormWrapper.propTypes = {
  orgType: PropTypes.oneOf(Object.values(ORG_TYPES)).isRequired,
  user: PropTypes.shape(),
  userType: PropTypes.oneOf(Object.values(USER_TYPES)),
};

SignupFormWrapper.defaultProps = {
  user: null,
  userType: USER_TYPES.ADMINISTRATOR,
};

export default SignupFormWrapper;
